import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
  Button,
  Switch,
  Avatar,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

const BrandList = ({ onEdit, onAdd }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = () => {
    setLoading(true);
    axios.get('/api/brands')
      .then(res => setBrands(res.data.data || res.data))
      .catch(err => console.error('Error loading brands:', err))
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(`/api/brands/${id}/status`, { status: newStatus });
      setBrands(brands.map(brand => brand.id === id ? { ...brand, status: newStatus ? '1' : '0' } : brand));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update brand status');
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`/api/brands/${id}`);
        setBrands(brands.filter(brand => brand.id !== id));
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete brand');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBrands = filteredBrands.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);

  return (
    <Box>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.875rem'
          }}
        >
          <LocalFireDepartmentIcon sx={{ fontSize: 18 }} />
          All Brands ({filteredBrands.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              width: '180px',
              '& .MuiOutlinedInput-root': {
                fontSize: '0.8rem'
              }
            }}
          />
          <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ 
            bgcolor: '#f57c00',
            fontSize: '0.75rem',
            py: 0.5,
            px: 1.5,
            '&:hover': { 
              bgcolor: '#e65100'
            }
          }}
        >
          Add Brand
        </Button>
        </Box>
      </Box>

      {brands.length === 0 ? (
        <Paper sx={{ py: 3, px: 2, textAlign: 'center', bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
          <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            No brands found. Create your first brand above.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f5f5f5'
                }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBrands.map((brand) => (
                  <TableRow 
                    key={brand.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'action.hover'
                      },
                      '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1.5,
                        fontSize: '0.8rem'
                      }
                    }}
                  >
                    <TableCell>{brand.id}</TableCell>
                    <TableCell>
                      <Avatar 
                        src={brand.image}
                        sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                      >
                        {brand.name.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        {brand.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={brand.status === '1' || brand.status === 1}
                        onChange={(e) => handleStatusChange(brand.id, e.target.checked)}
                        color="secondary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        onClick={() => onEdit(brand)}
                        size="small"
                        sx={{ 
                        color: '#1976d2',
                        '&:hover': { 
                          bgcolor: '#e3f2fd'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(brand.id, brand.name)}
                        size="small"
                        sx={{ 
                          color: '#d32f2f',
                          ml: 0.5,
                          '&:hover': { 
                            bgcolor: '#ffebee'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default BrandList;
